import {
  className,
  edgeNameOf,
  jurisdictionName,
  type AlertMessage,
  type County,
  type HotlistRow,
} from '@hotlist/shared';

/**
 * The alert feed and the hotlist editor.
 *
 * DESIGN.md section 1: enforcement gets pinged the moment a hotlist plate
 * passes a camera, with the camera's position. What it never gets is a
 * continuous track. The alert says *seen here, this second*, and one second
 * later the vehicle is somewhere in a widening circle of road.
 *
 * So the feed shows an age that keeps counting, and next to it how far the car
 * could be by now. That number is the game: it is not a search radius the game
 * invented, it is speed multiplied by the time since the read, and it grows
 * while you look at it.
 */

export interface AlertHandlers {
  add: (plate: string, reason: string, note: string) => void;
  clear: (plate: string) => void;
  /**
   * Pan the map to the camera that fired.
   *
   * An alert names a road and a heading, which places the read for somebody
   * who knows the county and places nothing for somebody who does not -- and
   * even the local was reported taking a minute to find Lannon Road by eye.
   * The console knows the junction; the row is the way to look at it.
   */
  look?: (node: number) => void;
}

interface Held {
  alert: AlertMessage;
  /** Frame time it arrived, for the age that keeps counting. */
  arrived: number;
}

/** Most alerts kept on screen. Older ones scroll away, as they would. */
const MAX_HELD = 40;

export class AlertPanel {
  private on = false;
  private readonly feed: HTMLElement;
  private readonly list: HTMLElement;
  private readonly form: HTMLFormElement;
  private readonly plateInput: HTMLInputElement;
  private readonly reasonInput: HTMLSelectElement;
  private readonly noteInput: HTMLInputElement;
  private readonly county: County;
  private readonly held: Held[] = [];
  private readonly handlers: AlertHandlers;
  /** A plate the operator is looking at; its rows pulse until this clears. */
  private focused: string | null = null;
  private lastPainted = 0;

  constructor(county: County, handlers: AlertHandlers) {
    this.county = county;
    this.handlers = handlers;
    this.feed = must('alert-feed');
    this.list = must('hotlist-entries');
    this.form = must('hotlist-form') as HTMLFormElement;
    this.plateInput = must('hotlist-plate') as HTMLInputElement;
    this.reasonInput = must('hotlist-reason') as HTMLSelectElement;
    this.noteInput = must('hotlist-note') as HTMLInputElement;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      const plate = this.plateInput.value.trim().toUpperCase();
      if (!plate) return;
      handlers.add(plate, this.reasonInput.value, this.noteInput.value.trim());
      this.plateInput.value = '';
      this.noteInput.value = '';
    });

    this.list.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const plate = target.dataset.clear;
      if (plate) handlers.clear(plate);
    });
  }

  /**
   * Marks this panel live. Visibility belongs to the console, which owns the
   * tabs: two things deciding whether one element is on screen is how a panel
   * ends up hidden behind the tab that is supposed to show it.
   */
  enable(): void {
    this.on = true;
  }

  /** Put a plate in the box, so a car you just read can go on the list. */
  prefill(plate: string): void {
    this.plateInput.value = plate;
  }

  /**
   * Another alert.
   *
   * Every one is kept, including the fourth from the same plate this hour.
   *
   * Collapsing repeats would be an obvious kindness and would delete the best
   * evidenced finding this game has. CALIBRATION.md, from the Vallejo trial:
   * ALPR found as many as four times more flagged plates, *many of them
   * duplicates*, and that "may have desensitized officers to legitimate hits."
   * Measured here: 84% of the alert stream is the same car again, every repeat
   * inside an hour, worst offender nineteen times.
   *
   * So the feed does not tidy it up and does not annotate it either. Marking
   * something as a repeat would be the interface doing the noticing, and the
   * finding is precisely that nobody does the noticing.
   */
  /**
   * Light up every alert for one plate, and say whether there were any.
   *
   * The administrator's cross-link: clicking a listed car on the map should
   * land the eye on this plate's alerts, not on a feed the eye then has to
   * search. Null clears it. Returns whether anything matched, so the caller
   * knows whether opening the console is worth the jolt.
   */
  focus(plate: string | null): boolean {
    this.focused = plate;
    this.lastPainted = 0;
    if (plate === null) return false;
    return this.held.some((entry) => entry.alert.plate === plate);
  }

  receive(alert: AlertMessage): void {
    this.held.unshift({ alert, arrived: performance.now() });
    if (this.held.length > MAX_HELD) this.held.length = MAX_HELD;
    this.lastPainted = 0;
  }

  showHotlist(entries: HotlistRow[]): void {
    this.list.replaceChildren();
    if (entries.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'query-empty';
      empty.textContent = 'Nothing on the list.';
      this.list.append(empty);
      return;
    }
    for (const entry of entries) {
      const row = document.createElement('div');
      row.className = entry.stale ? 'hotlist-row stale' : 'hotlist-row';

      const plate = document.createElement('span');
      plate.className = 'hotlist-plate';
      plate.textContent = entry.plate;

      const why = document.createElement('span');
      why.className = 'hotlist-why';
      // A stale entry looks exactly like a live one from the console, which is
      // the whole problem. It is marked here only because this is a game and
      // somebody has to be able to learn the lesson.
      why.textContent = entry.stale ? `${entry.reason} (already resolved)` : entry.reason;

      const clear = document.createElement('button');
      clear.type = 'button';
      clear.textContent = 'clear';
      clear.dataset.clear = entry.plate;

      row.append(plate, why, clear);
      this.list.append(row);
    }
  }

  /**
   * Repaint the feed. Called every frame because the ages are moving, and the
   * ages are the information.
   */
  update(now: number): void {
    if (now - this.lastPainted < 250) return;
    this.lastPainted = now;

    this.feed.replaceChildren();
    if (this.held.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'query-empty';
      empty.textContent = 'No alerts.';
      this.feed.append(empty);
      return;
    }

    for (const { alert, arrived } of this.held) {
      const age = (now - arrived) / 1000;
      const line = document.createElement('div');
      line.className =
        this.focused !== null && alert.plate === this.focused
          ? 'alert-row alert-focus'
          : 'alert-row';
      line.addEventListener('click', () => this.handlers.look?.(alert.node));

      const head = document.createElement('div');
      head.className = 'alert-head';
      const plate = document.createElement('span');
      plate.className = 'alert-plate';
      plate.textContent = alert.plate;
      const reason = document.createElement('span');
      reason.className = 'alert-reason';
      reason.textContent = alert.reason;
      head.append(plate, reason);

      const where = document.createElement('div');
      where.className = 'alert-where';
      // Road, town, heading. The town is what places the read for anybody who
      // does not already know which road is which.
      where.textContent =
        `${placeOf(this.county, alert.node)}, ` +
        `${townOf(this.county, alert.node)}, heading ${compass(alert.bearing)}`;

      // What the record says is wanted. Aurora, 2020: a Colorado plate matched
      // a stolen motorcycle on Montana plates and nobody checked either field.
      // Both are on the screen here. Nothing makes anybody read them.
      const wanted = document.createElement('div');
      wanted.className = 'alert-wanted';
      // The issuing state in full. "IA" asks the operator to know nine postal
      // codes; Aurora is the case where nobody checked this exact field.
      wanted.textContent = alert.wanted
        ? `wanted: ${jurisdictionName(alert.jurisdiction)} plate, ${alert.wanted}`
        : `wanted: ${jurisdictionName(alert.jurisdiction)} plate, no description on file`;

      // The widening circle. Speed at the read, times how long ago it was.
      const drift = document.createElement('div');
      drift.className = 'alert-drift';
      drift.textContent =
        age < 1
          ? 'seen just now'
          : `${Math.round(age)}s ago, could be ${reach(alert.speed * age)} from there by now`;

      line.append(head, where, wanted, drift);
      this.feed.append(line);
    }
  }
}

/** The nearest named settlement to a junction, for placing a read. */
function townOf(county: County, node: number): string {
  const x = county.nodeX[node] as number;
  const y = county.nodeY[node] as number;
  let best = '';
  let bestAway = Infinity;
  for (const place of county.places) {
    const away = Math.hypot(place.x - x, place.y - y);
    if (away < bestAway) {
      bestAway = away;
      best = place.name;
    }
  }
  return best ? `near ${best}` : 'location unknown';
}

function placeOf(county: County, node: number): string {
  if (node < 0 || node >= county.nodeCount) return 'unknown';
  const start = county.adjStart[node] as number;
  const end = county.adjStart[node + 1] as number;
  const names: string[] = [];
  for (let i = start; i < end; i++) {
    const name = edgeNameOf(county, county.adjEdge[i] as number);
    if (name && !names.includes(name)) names.push(name);
  }
  if (names.length >= 2) return `${names[0]} at ${names[1]}`;
  if (names.length === 1) return names[0] as string;
  return className(county.edgeClass[county.adjEdge[start] as number] as number);
}

/**
 * How far, in the units the county uses.
 *
 * Everything inside the simulation is meters, because the road graph is meters
 * and arithmetic on mixed units is how things go wrong quietly. Everything a
 * player reads is feet and miles, because this is Wisconsin.
 *
 * Feet up to a fifth of a mile. "0.1 miles" is not a distance anybody acts on
 * and "530 feet" is, and at the moment an alert lands the small numbers are
 * the ones that matter.
 */
function reach(meters: number): string {
  const feet = meters * 3.28084;
  if (feet < 1056) return `${Math.round(feet / 10) * 10} feet`;
  const miles = meters / 1609.344;
  return `${miles.toFixed(miles < 10 ? 1 : 0)} miles`;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

function compass(bearing: number): string {
  return COMPASS[Math.round(bearing / 45) % 8] as string;
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
